import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { BrowserRouter, Navigate, NavLink, Route, Routes, useNavigate } from 'react-router';
import { useLogout } from './api/staff.js';
import { Can, CanAny, RequireAuth, useCurrentUser } from './auth/session.js';
import { t } from './i18n/uk.js';
import { AuditPage } from './pages/AuditPage.js';
import { CataloguePage } from './pages/CataloguePage.js';
import { CustomerCardPage } from './pages/CustomerCardPage.js';
import { CustomersPage } from './pages/CustomersPage.js';
import { LoginPage } from './pages/LoginPage.js';
import { LoyaltyPage } from './pages/LoyaltyPage.js';
import { OrderPage } from './pages/OrderPage.js';
import { OrdersPage } from './pages/OrdersPage.js';
import { PaymentsPage } from './pages/PaymentsPage.js';
import { PricingPage } from './pages/PricingPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { SystemStatusPage } from './pages/SystemStatusPage.js';
import { UsersPage } from './pages/UsersPage.js';

const NoAccess = () => <p className="error-text">{t.common.noAccess}</p>;

export const createQueryClient = () =>
  new QueryClient({ defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } } });

function UserMenu() {
  const me = useCurrentUser();
  const logout = useLogout();
  const navigate = useNavigate();
  return (
    <div className="user-menu">
      <span>
        {me.name} <span className="muted">· {t.roles[me.role]}</span>
      </span>
      <button
        type="button"
        onClick={() => logout.mutate(undefined, { onSuccess: () => void navigate('/login') })}
      >
        {t.nav.logout}
      </button>
    </div>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  return (
    <div className="shell">
      <header className="topbar">
        <span className="brand">{t.appName}</span>
        <nav>
          <CanAny permissions={['orders.edit', 'shipments.manage']}>
            <NavLink to="/orders">{t.nav.orders}</NavLink>
          </CanAny>
          <CanAny permissions={['payments.match', 'payments.refund']}>
            <NavLink to="/payments">{t.nav.payments}</NavLink>
          </CanAny>
          <Can permission="customers.manage">
            <NavLink to="/customers">{t.nav.customers}</NavLink>
          </Can>
          <NavLink to="/catalogue">{t.nav.catalogue}</NavLink>
          <Can permission="pricing.edit">
            <NavLink to="/pricing">{t.nav.pricing}</NavLink>
          </Can>
          <NavLink to="/system">{t.nav.system}</NavLink>
          <Can permission="users.manage">
            <NavLink to="/users">{t.nav.users}</NavLink>
          </Can>
          <Can permission="settings.manage">
            <NavLink to="/loyalty">{t.nav.loyalty}</NavLink>
          </Can>
          <Can permission="settings.manage">
            <NavLink to="/settings">{t.nav.settings}</NavLink>
          </Can>
          <Can permission="audit.view">
            <NavLink to="/audit">{t.nav.audit}</NavLink>
          </Can>
        </nav>
        <UserMenu />
      </header>
      <main className="content">{children}</main>
    </div>
  );
}

/** Pages behind login, each also behind its permission (the API enforces the same rules). */
function Protected() {
  return (
    <RequireAuth>
      <Layout>
        <Routes>
          <Route path="/" element={<Navigate to="/system" replace />} />
          <Route path="/system" element={<SystemStatusPage />} />
          <Route path="/catalogue" element={<CataloguePage />} />
          <Route
            path="/orders"
            element={
              <CanAny permissions={['orders.edit', 'shipments.manage']} fallback={<NoAccess />}>
                <OrdersPage />
              </CanAny>
            }
          />
          <Route
            path="/orders/:id"
            element={
              <CanAny permissions={['orders.edit', 'shipments.manage']} fallback={<NoAccess />}>
                <OrderPage />
              </CanAny>
            }
          />
          <Route
            path="/payments"
            element={
              <CanAny permissions={['payments.match', 'payments.refund']} fallback={<NoAccess />}>
                <PaymentsPage />
              </CanAny>
            }
          />
          <Route
            path="/customers"
            element={
              <Can permission="customers.manage" fallback={<NoAccess />}>
                <CustomersPage />
              </Can>
            }
          />
          <Route
            path="/customers/:id"
            element={
              <Can permission="customers.manage" fallback={<NoAccess />}>
                <CustomerCardPage />
              </Can>
            }
          />
          <Route
            path="/pricing"
            element={
              <Can permission="pricing.edit" fallback={<NoAccess />}>
                <PricingPage />
              </Can>
            }
          />
          <Route
            path="/users"
            element={
              <Can permission="users.manage" fallback={<NoAccess />}>
                <UsersPage />
              </Can>
            }
          />
          <Route
            path="/loyalty"
            element={
              <Can permission="settings.manage" fallback={<NoAccess />}>
                <LoyaltyPage />
              </Can>
            }
          />
          <Route
            path="/settings"
            element={
              <Can permission="settings.manage" fallback={<NoAccess />}>
                <SettingsPage />
              </Can>
            }
          />
          <Route
            path="/audit"
            element={
              <Can permission="audit.view" fallback={<NoAccess />}>
                <AuditPage />
              </Can>
            }
          />
          <Route path="*" element={<Navigate to="/system" replace />} />
        </Routes>
      </Layout>
    </RequireAuth>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/*" element={<Protected />} />
    </Routes>
  );
}

export function App() {
  return (
    <QueryClientProvider client={createQueryClient()}>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
